import { db } from "./firebase";
import {
  findCuratedDoseLimit,
  doseLimitFromReference,
} from "data/medicationDoseLimits";

// Written only by the syncDrugReference scheduled function (functions/src/openfda/sync.js),
// readable by any signed-in user. Public label data, no PHI.
const REFERENCE_COLLECTION = "drugReference";
const LABEL_COLLECTION = "drugLabels";
// Brand names the medication search does not offer (Coumadin), each pointing at the
// drugReference entry of its ingredient. See functions/src/openfda/aliases.js.
const ALIAS_COLLECTION = "drugAliases";

/**
 * The drugAliases id for a medication name: lowercase words separated by single spaces. Must
 * match aliasKey in functions/src/openfda/aliases.js.
 */
export const aliasKey = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * What a medication is looked up by: its stored rxcui when it was picked from the search, else
 * its name, resolved through the brand-name aliases (a hand-typed "Coumadin"). Null when there
 * is neither. A stable id for caches and effect dependencies, not a Firestore id.
 */
export const lookupId = ({ rxcui, name } = {}) =>
  rxcui ? `rxcui:${rxcui}` : aliasKey(name) ? `name:${aliasKey(name)}` : null;

// Label data changes monthly at most, so a lookup is cached for the life of the page. Promises
// are cached rather than results, so concurrent callers share one read.
const referenceCache = new Map();
const labelCache = new Map();
const aliasCache = new Map();

function cachedGet(cache, collection, id) {
  // Firestore ids cannot contain "/"; such a key can never have been written.
  if (!id || String(id).includes("/")) return Promise.resolve(null);
  const key = String(id);
  if (!cache.has(key)) {
    cache.set(
      key,
      // Started from a resolved promise so a synchronous throw lands in the catch below too.
      Promise.resolve()
        .then(() => db.collection(collection).doc(key).get())
        .then((snap) => (snap.exists ? { id: snap.id, ...snap.data() } : null))
        .catch((error) => {
          // Not cached: a transient failure should not hide the entry for the whole session.
          cache.delete(key);
          console.error(`Error reading ${collection}/${key}:`, error);
          return null;
        })
    );
  }
  return cache.get(key);
}

export const drugReferenceService = {
  /**
   * The drugReference entry for a medication's stored `rxcui` (the id the medication search
   * saves, e.g. "6809-oral-pill"), or null when there is none.
   */
  getReference(rxcui) {
    return cachedGet(referenceCache, REFERENCE_COLLECTION, rxcui);
  },

  /** The drugAliases entry for a medication name, or null. */
  getAlias(name) {
    return cachedGet(aliasCache, ALIAS_COLLECTION, aliasKey(name));
  },

  /**
   * The drugReference entry for a medication: by its stored rxcui when it was picked from the
   * search, else by its name through drugAliases (a hand-typed "Coumadin" reaches warfarin).
   */
  async getReferenceFor({ rxcui, name } = {}) {
    if (rxcui) return this.getReference(rxcui);
    const alias = name ? await this.getAlias(name) : null;
    return alias?.referenceKey ? this.getReference(alias.referenceKey) : null;
  },

  /** The trimmed openFDA label a reference points at (`labelSetId`), or null. */
  getLabel(setId) {
    return cachedGet(labelCache, LABEL_COLLECTION, setId);
  },

  /**
   * The dose reference for a medication: a curated entry if there is one, else the openFDA
   * ceiling from drugReference, else null.
   */
  async getDoseLimit({ rxcui, name } = {}) {
    const curated = findCuratedDoseLimit({ rxcui, name });
    if (curated) return curated;
    return doseLimitFromReference(await this.getReferenceFor({ rxcui, name }));
  },

  /** For tests. */
  clearCache() {
    referenceCache.clear();
    labelCache.clear();
    aliasCache.clear();
  },
};
